import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useLocation } from 'react-router-dom'
import { Context as GeneralContext } from '@/context'
import { Context as LoadingContext } from '@/context/loading'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Footer from '@/components/Footer'
import Meta from '@/components/Meta'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { externalPreload } from '@/utils/dom'
import { getSlug } from '@/utils/path'
import constants from '@/constants'
import PageBuilder, { BigText } from './PageBuilder'
import RelatedProducts from './RelatedProducts'
import Cover from './Cover'
import style from './style'

const useStyles = createUseStyles(style)

const EditorialSEO = () => {
  const { getImageUrl, headerHeight } = useContext(GeneralContext)
  const { setReady, ready } = useContext(LoadingContext)
  const classes = useStyles({ headerHeight })
  const [load, setLoad] = useState(false)
  const [relatedProduct, setRelatedProduct] = useState([])
  const $root = useRef()
  const location = useLocation()
  const stub = useRef(getSlug(location.pathname))

  /*------------------------------
  Hooks to handle media loading & updating the scrollbar
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, mediaQueryKey, strings, categories } = useSelector((state) => ({
    current: state.page.current.find,
    page: state.page.pages[stub.current] || {},
    mediaQueryKey: state.mediaQuery.key,
    strings: state.language.string,
    categories: state.shop.categories || [],
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const getEcwidRelatedProduct = useCallback((ids) => dispatch(actions.shop.getEcwidRelatedProduct(ids)), [dispatch])
  const setStoreProducts = useCallback((items) => dispatch(actions.shop.setStoreProducts(items)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub.current) setCurrentPage(stub.current, 'pages')
    getPage(stub.current)
  }

  /*------------------------------
  If the page is loaded from redux, start to preload media
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      completePreload()

      if (page.ecwid_prodotto_id) {
        getEcwidRelatedProduct(page.ecwid_prodotto_id.trim())
          .then((res) => {
            setRelatedProduct(res.map((r) => ({
              ...r,
              title: r.name,
              link: `${constants.urls.SITE}${window.pathSite}shop/${r.id}`,
              designer: categories?.find((cat) => cat.id === r.categoryIds[0])?.name,
              image: r.originalImageUrl,
              price: r.defaultDisplayedPriceFormatted,
              discountPrice: r.compareToPriceFormatted,
              isInStock: r.inStock,
            })))
          })
      }
    }
  }, [page])

  useEffect(() => {
    if (relatedProduct.length > 0) setStoreProducts(relatedProduct)
  }, [relatedProduct])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        <Cover
          title={page.title.rendered.split('<br/>')}
          img={getImageUrl(page.featured_media, 'full', stub.current, 'pages')}
          ready={ready}
        />
        <BigText item={page.content.rendered} />
        <PageBuilder page={page} stub={stub.current} mediaQueryKey={mediaQueryKey} strings={strings} />
        <RelatedProducts items={relatedProduct} />
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageEditorial ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default EditorialSEO
